import { useEffect, useState, useCallback, createElement } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Eye, UploadCloud } from "lucide-react";
import { dicts, deepMerge, enterPreview } from "../../i18n/LanguageContext";
import { authHeader } from "../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LANGS = [{ code: "en", label: "English" }, { code: "el", label: "Ελληνικά" }, { code: "ru", label: "Русский" }];

export const setDeep = (obj, path, val) => {
  if (path.length === 0) return val;
  const [h, ...rest] = path;
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  clone[h] = setDeep(obj[h], rest, val);
  return clone;
};

const labelFor = (key) => String(key).replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2");

const FieldNode = ({ value, path, onSet }) => {
  if (typeof value === "string") {
    const key = path[path.length - 1];
    const long = value.length > 70;
    const cls = "w-full border border-[#E3DED4] px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#B85D2A]";
    return (
      <div className="mb-3">
        <label className="block font-mono text-[10px] uppercase tracking-[0.14em] text-[#8C6B4F] mb-1">{labelFor(key)}</label>
        {long ? (
          <textarea rows={3} className={cls} value={value} onChange={(e) => onSet(path, e.target.value)} />
        ) : (
          <input className={cls} value={value} onChange={(e) => onSet(path, e.target.value)} />
        )}
      </div>
    );
  }
  if (typeof value === "number") {
    return (
      <div className="mb-3">
        <label className="block font-mono text-[10px] uppercase tracking-[0.14em] text-[#8C6B4F] mb-1">{labelFor(path[path.length - 1])}</label>
        <input type="number" className="w-40 border border-[#E3DED4] px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#B85D2A]" value={value} onChange={(e) => onSet(path, Number(e.target.value))} />
      </div>
    );
  }
  if (Array.isArray(value)) {
    return (
      <div className="pl-4 border-l-2 border-[#EBE7DF] mb-4">
        {value.map((v, i) => (
          <div key={i} className="mb-2">
            {typeof v === "object" && v !== null && <p className="font-mono text-[10px] text-[#B85D2A] mb-1">— {i + 1} —</p>}
            {createElement(FieldNode, { value: v, path: [...path, i], onSet })}
          </div>
        ))}
      </div>
    );
  }
  if (typeof value === "object" && value !== null) {
    return (
      <div className="mb-2">
        {Object.entries(value).map(([k, v]) => createElement(FieldNode, { key: k, value: v, path: [...path, k], onSet }))}
      </div>
    );
  }
  return null;
};

export const useContentDoc = (lang) => {
  const [content, setContent] = useState(null);
  useEffect(() => {
    setContent(null);
    axios.get(`${API}/content/${lang}`, { params: { mode: "draft" }, headers: authHeader() })
      .then((r) => setContent(deepMerge(dicts[lang], r.data || {})))
      .catch(() => setContent(dicts[lang]));
  }, [lang]);
  const saveDraft = useCallback(async (data) => {
    await axios.put(`${API}/content/${lang}`, data, { headers: authHeader() });
  }, [lang]);
  const publish = useCallback(async (data) => {
    await axios.put(`${API}/content/${lang}`, data, { headers: authHeader() });
    await axios.post(`${API}/content/${lang}/publish`, {}, { headers: authHeader() });
  }, [lang]);
  return { content, setContent, saveDraft, publish };
};

export const LangTabs = ({ lang, setLang }) => (
  <div className="flex gap-1 mb-6">
    {LANGS.map((l) => (
      <button key={l.code} data-testid={`content-lang-${l.code}`} onClick={() => setLang(l.code)} className={`px-4 py-2 text-xs font-medium transition-colors ${lang === l.code ? "bg-[#1A2533] text-[#FBF9F5]" : "bg-white border border-[#E3DED4] text-[#526071] hover:text-[#121926]"}`}>
        {l.label}
      </button>
    ))}
  </div>
);

export const EditorToolbar = ({ onSaveDraft, onPublish, busy, prefix }) => {
  const previewSite = async () => {
    await onSaveDraft(true);
    enterPreview();
    window.open("/", "_blank");
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button onClick={() => onSaveDraft()} disabled={busy} data-testid={`${prefix}-save-button`} className="inline-flex items-center gap-2 px-5 py-3 bg-white border border-[#1A2533]/30 text-[#1A2533] text-xs font-medium hover:border-[#B85D2A] hover:text-[#B85D2A] transition-colors disabled:opacity-60">
        <Save size={14} /> Save Draft
      </button>
      <button onClick={previewSite} disabled={busy} data-testid={`${prefix}-preview-button`} className="inline-flex items-center gap-2 px-5 py-3 bg-[#1A2533] text-white text-xs font-medium hover:bg-[#253549] transition-colors disabled:opacity-60">
        <Eye size={14} /> Preview Site
      </button>
      <button onClick={onPublish} disabled={busy} data-testid={`${prefix}-publish-button`} className="inline-flex items-center gap-2 px-5 py-3 bg-[#B85D2A] text-white text-xs font-medium hover:bg-[#A24E20] transition-colors disabled:opacity-60">
        <UploadCloud size={14} /> Publish
      </button>
    </div>
  );
};

export const ContentEditor = () => {
  const [lang, setLang] = useState("en");
  const { content, setContent, saveDraft, publish } = useContentDoc(lang);
  const [busy, setBusy] = useState(false);

  const onSet = (path, val) => setContent((c) => setDeep(c, path, val));

  const doSaveDraft = async (silent) => {
    setBusy(true);
    try {
      await saveDraft(content);
      if (!silent) toast.success("Draft saved. Use Preview to review, Publish to go live.");
    } catch {
      toast.error("Save failed. Are you still signed in?");
    } finally {
      setBusy(false);
    }
  };

  const doPublish = async () => {
    setBusy(true);
    try {
      await publish(content);
      toast.success(`Published — ${lang.toUpperCase()} content is now live.`);
    } catch {
      toast.error("Publish failed. Are you still signed in?");
    } finally {
      setBusy(false);
    }
  };

  if (!content) return <p className="font-mono text-sm text-[#8A94A2]">Loading content...</p>;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-3">
        <div>
          <h2 className="font-serif text-xl text-[#121926]">Page Content</h2>
          <p className="text-xs text-[#718096] mt-1">Edits are saved as a draft. Preview shows the draft on the live site; Publish makes it public.</p>
        </div>
        <EditorToolbar onSaveDraft={doSaveDraft} onPublish={doPublish} busy={busy} prefix="content" />
      </div>
      <LangTabs lang={lang} setLang={setLang} />
      <div className="space-y-3">
        {Object.entries(content).filter(([k]) => k !== "seo").map(([section, value]) => (
          <details key={`${lang}-${section}`} className="bg-white border border-[#E3DED4]" data-testid={`content-section-${section}`}>
            <summary className="cursor-pointer px-5 py-4 font-serif text-base text-[#121926] capitalize select-none hover:text-[#B85D2A]">{labelFor(section)}</summary>
            <div className="px-5 pb-5 pt-2 border-t border-[#EDEAE1]">
              <FieldNode value={value} path={[section]} onSet={onSet} />
            </div>
          </details>
        ))}
      </div>
    </div>
  );
};
